import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { Mic, MonitorPlay, PenLine } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useSession } from '@/hooks/useSession';
import type { BugReportCategory, BugReportMode, BugReportStatus } from '@/api/bug-reports';

/**
 * Route wall for /admin/*. The nav item is hidden by `platformAdmin` in
 * nav.ts and the API 404s for anyone else; this sends a curious URL-typer to
 * the timesheet as if the screen did not exist.
 */
export function PlatformAdminOnly({ children }: { children: ReactNode }) {
  const session = useSession();
  if (!session.isPlatformAdmin) return <Navigate to="/timesheet" replace />;
  return <>{children}</>;
}

export const STATUS_LABEL: Record<BugReportStatus, string> = {
  open: 'Open',
  addressed: 'Addressed',
  wont_fix: 'Won’t fix'
};

export const CATEGORY_LABEL: Record<BugReportCategory, string> = {
  bug: 'Bug',
  ui: 'Looks wrong',
  feature: 'Idea',
  question: 'Question'
};

export function BugStatusBadge({ status }: { status: BugReportStatus }) {
  const tone = status === 'open' ? 'warning' : status === 'addressed' ? 'positive' : 'outline';
  return <Badge tone={tone}>{STATUS_LABEL[status] ?? status}</Badge>;
}

export function CategoryBadge({ category }: { category: BugReportCategory }) {
  return <Badge tone={category === 'bug' ? 'danger' : 'neutral'}>{CATEGORY_LABEL[category] ?? category}</Badge>;
}

export function ModeIcon({ mode, className }: { mode: BugReportMode; className?: string }) {
  const Icon = mode === 'screen_audio' ? Mic : mode === 'screen' ? MonitorPlay : PenLine;
  const label = mode === 'screen_audio' ? 'Screen and voice' : mode === 'screen' ? 'Screen recording' : 'Text only';
  return <Icon aria-label={label} className={className ?? 'size-4 text-[var(--text-subtle)]'} />;
}

export function pathOf(url: string | null) {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    return parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}

export function formatWhen(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
